import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { MarketingHead } from "@/components/marketing/MarketingHead";
import { LandingNav } from "@/components/landing/LandingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { useSiteTheme } from "@/hooks/useSiteTheme";
import { HOMEPAGE_HTML, HOMEPAGE_STYLE } from "@/components/homepage/homepageContent";

/**
 * Homepage — the public marketing landing page at `/`.
 *
 * A React port of the Claude Design "Homepage.dc.html" import, restructured
 * 2026-08-01 so the packet scanner leads: scanner hero, sealed-record proof
 * section, one-number verify demo, monitoring feed, pricing, FAQ. The design's
 * markup lives in src/components/homepage/homepageContent.ts and is injected
 * here; the effect below re-implements its behavior (the three looping demos,
 * the FAQ accordion, scroll reveals, stat counters) against the injected DOM
 * and wires every placeholder link to a real route.
 *
 * Chrome: the shared <LandingNav> renders the header (same links as the rest of
 * the marketing site). Theme: light/dark comes from the global SiteThemeProvider
 * (data-cr-theme on <html> → the --cr-* tokens in index.css); the homepage
 * defaults to light. The header carries the toggle.
 *
 * ANALYTICS / CONSENT: Google Ads (gtag), the Meta Pixel, and the cookie-consent
 * banner are mounted at the App level and initialized in src/main.tsx — they sit
 * outside the route element, so this page does not touch them.
 */

// Placeholder-link → real destination, keyed by the anchor's trimmed text. Hash
// links (#how, #proof, #verify, …) are left alone so in-page smooth-scroll still
// works. The hero CTAs, the proof-section CTAs and the header/footer links carry
// real hrefs already (the shared LandingNav / LandingFooter own their own links),
// so only the in-body placeholder CTAs are mapped here.
const LINK_MAP: Record<string, string> = {
  "Start free": "/signup",
  "Claim profile": "/carriers/signup",
  "Talk to us": "mailto:support@cipherandrow.com",
  "See full pricing →": "/pricing",
};

// The design uses Inter Tight (display), Inter (body), and JetBrains Mono. Those
// families are declared as CSS variables in index.css but not otherwise loaded,
// so this page pulls them from Google Fonts. Injected directly into <head>
// (mirroring usePageMeta's approach) rather than via react-helmet so the page
// still renders in bare, provider-light unit tests.
const FONT_LINKS: Array<Partial<HTMLLinkElement> & { id: string }> = [
  { id: "cr-font-pre-1", rel: "preconnect", href: "https://fonts.googleapis.com" },
  { id: "cr-font-pre-2", rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
  {
    id: "cr-font-css",
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Inter:wght@400;450;500;600&family=JetBrains+Mono:wght@400;500&display=swap",
  },
];

function ensureFonts() {
  if (typeof document === "undefined") return;
  for (const spec of FONT_LINKS) {
    if (document.getElementById(spec.id)) continue;
    const link = document.createElement("link");
    link.id = spec.id;
    if (spec.rel) link.rel = spec.rel;
    if (spec.href) link.href = spec.href;
    if (spec.crossOrigin) link.crossOrigin = spec.crossOrigin;
    document.head.appendChild(link);
  }
}

const Index = () => {
  const navigate = useNavigate();
  const { theme } = useSiteTheme();
  const hostRef = useRef<HTMLDivElement>(null);

  usePageMeta({
    title: "Carrier & Broker Verification 2026 · Canada-First Trust Platform",
    description:
      "Scan a carrier packet or verify any US or Canadian carrier and broker, and seal every check into a signed, independently timestamped record you can produce later. Continuous monitoring alerts when authority, bond, or insurance changes. Free for carriers.",
    path: "/",
    // ?v=3 matches index.html's static og:image so hydration doesn't swap the
    // social-scraper cache-buster back off.
    image: "https://www.cipherandrow.com/og/home.jpg?v=3",
  });

  useEffect(() => {
    ensureFonts();
  }, []);

  // The inline R-shield marks (monitoring card + footer) swap black/white with
  // the theme. The rest of the page is themed purely through the --cr-* tokens.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const src = theme === "light" ? "/cr-logo-icon-black.svg" : "/cr-logo-icon-white.svg";
    host.querySelectorAll<HTMLImageElement>("[data-cr-logo]").forEach((el) => el.setAttribute("src", src));
  }, [theme]);

  useEffect(() => {
    const host = hostRef.current;
    const root = host?.querySelector<HTMLElement>("[data-cr-root]");
    if (!root) return;

    const q = (s: string) => root.querySelector<HTMLElement>(s);
    const qa = (s: string) => Array.from(root.querySelectorAll<HTMLElement>(s));

    const demoTimers: ReturnType<typeof setTimeout>[] = [];
    const scanTimers: ReturnType<typeof setTimeout>[] = [];
    const sealTimers: ReturnType<typeof setTimeout>[] = [];
    const cleanups: Array<() => void> = [];

    // ── FAQ accordion ────────────────────────────────────────────────────────
    root.querySelectorAll<HTMLElement>("[data-cr-faqlist] > div").forEach((item) => {
      const btn = item.querySelector<HTMLElement>("[data-faq-q]");
      const ans = item.querySelector<HTMLElement>("[data-faq-answer]");
      const icon = item.querySelector<HTMLElement>("[data-faq-icon]");
      if (!btn || !ans) return;
      const onClick = () => {
        const open = ans.style.maxHeight && ans.style.maxHeight !== "0px";
        if (open) {
          ans.style.maxHeight = "0px";
          if (icon) icon.style.transform = "rotate(0deg)";
        } else {
          ans.style.maxHeight = ans.scrollHeight + "px";
          if (icon) icon.style.transform = "rotate(45deg)";
        }
      };
      btn.addEventListener("click", onClick);
      cleanups.push(() => btn.removeEventListener("click", onClick));
    });

    // ── stat counters ────────────────────────────────────────────────────────
    const runCounters = (scope: HTMLElement) => {
      scope.querySelectorAll<HTMLElement & { _done?: boolean }>("[data-count]").forEach((el) => {
        if (el._done) return;
        el._done = true;
        const target = parseFloat(el.getAttribute("data-count") || "0");
        const suffix = el.getAttribute("data-suffix") || "";
        const dur = 1200;
        const start = performance.now();
        const step = (now: number) => {
          const p = Math.min(1, (now - start) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          const v = Math.round(target * eased);
          el.textContent = v.toLocaleString() + suffix;
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    };

    // ── scroll reveals ─────────────────────────────────────────────────────────
    let io: IntersectionObserver | null = null;
    if (typeof IntersectionObserver !== "undefined") {
      const reveals = qa("[data-reveal]");
      reveals.forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.top > window.innerHeight * 0.82) {
          el.style.opacity = "0";
          el.style.transform = "translateY(22px)";
          el.style.transition =
            "opacity .6s cubic-bezier(.25,.4,.25,1), transform .6s cubic-bezier(.25,.4,.25,1)";
        }
      });
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            const t = e.target as HTMLElement;
            t.style.opacity = "1";
            t.style.transform = "translateY(0)";
            if (t.hasAttribute("data-cr-statgrid")) runCounters(t);
            io?.unobserve(t);
          });
        },
        { threshold: 0.18 },
      );
      reveals.forEach((el) => io!.observe(el));
      const grid = q("[data-cr-statgrid]");
      if (grid) io.observe(grid);
    }

    // ── hero demo (7 tabs → one verdict) ──────────────────────────────────────
    const runDemo = (loop: boolean) => {
      demoTimers.splice(0).forEach(clearTimeout);
      const tabs = qa("[data-cr-tab]");
      const crtab = q("[data-cr-crtab]");
      const before = q("[data-cr-before]");
      const verdict = q("[data-cr-verdict]");
      const phase = q("[data-cr-phase]");

      tabs.forEach((t) => {
        t.style.width = "";
        t.style.opacity = "1";
        t.style.paddingLeft = "11px";
        t.style.paddingRight = "11px";
        t.style.marginLeft = "0px";
        t.style.transitionDelay = "0ms";
      });
      if (crtab) {
        crtab.style.width = "0px";
        crtab.style.opacity = "0";
      }
      if (before) before.style.opacity = "1";
      if (verdict) {
        verdict.style.opacity = "0";
        verdict.style.transform = "translateY(10px) scale(.97)";
      }
      if (phase) {
        phase.textContent = "The old way";
        phase.style.color = "var(--cr-amber)";
      }

      demoTimers.push(
        setTimeout(() => {
          tabs.forEach((t, i) => {
            t.style.transitionDelay = i * 55 + "ms";
            t.style.width = "0px";
            t.style.opacity = "0";
            t.style.paddingLeft = "0px";
            t.style.paddingRight = "0px";
            t.style.marginLeft = "-6px";
          });
          if (crtab) {
            crtab.style.width = "132px";
            crtab.style.opacity = "1";
          }
          if (before) before.style.opacity = "0";
          if (phase) {
            phase.textContent = "Consolidating";
            phase.style.color = "var(--cr-fg-muted)";
          }
        }, 3700),
      );

      demoTimers.push(
        setTimeout(() => {
          if (verdict) {
            verdict.style.opacity = "1";
            verdict.style.transform = "translateY(0) scale(1)";
          }
          if (phase) {
            phase.textContent = "One verdict";
            phase.style.color = "var(--cr-mint)";
          }
        }, 4650),
      );

      if (loop) demoTimers.push(setTimeout(() => runDemo(true), 8600));
    };

    // ── packet scanner demo ────────────────────────────────────────────────────
    const runScanner = (loop: boolean) => {
      scanTimers.splice(0).forEach(clearTimeout);
      const fields = qa("[data-scan-field]");
      const flag = q("[data-scan-flag]");
      const verdict = q("[data-scan-verdict]");
      const phase = q("[data-scan-phase]");
      const show = (el: HTMLElement | null) => {
        if (el) {
          el.style.opacity = "1";
          el.style.transform = "translateX(0)";
        }
      };
      const hide = (el: HTMLElement | null) => {
        if (el) {
          el.style.opacity = "0";
          el.style.transform = "translateX(10px)";
        }
      };

      fields.forEach(hide);
      hide(flag);
      hide(verdict);
      if (phase) {
        phase.textContent = "Reading fields";
        phase.style.color = "var(--cr-mint)";
      }

      // Pacing (tightened 2026-08-01, founder note that the hero loop dragged):
      // fields land ~350ms apart, so the whole read → flag → seal arc runs in
      // roughly 3s and the loop restarts about every 6s.
      fields.forEach((f, i) => scanTimers.push(setTimeout(() => show(f), 380 + i * 350)));
      const afterFields = 380 + fields.length * 350;
      scanTimers.push(
        setTimeout(() => {
          if (phase) phase.textContent = "Cross-checking";
        }, afterFields),
      );
      scanTimers.push(
        setTimeout(() => {
          show(flag);
          if (phase) {
            phase.textContent = "Flag caught";
            phase.style.color = "var(--cr-amber)";
          }
        }, afterFields + 380),
      );
      scanTimers.push(
        setTimeout(() => {
          show(verdict);
          if (phase) {
            phase.textContent = "Sealed";
            phase.style.color = "var(--cr-mint)";
          }
        }, afterFields + 950),
      );

      if (loop) scanTimers.push(setTimeout(() => runScanner(true), afterFields + 3400));
    };

    // ── sealed-record verification demo (proof section) ────────────────────────
    // Mirrors what /verify/report/:reportNo actually does for a stranger with
    // the link: re-check the fingerprint, the Ed25519 signature, the RFC 3161
    // timestamp, and that the event log has no post-seal edits.
    const runSeal = (loop: boolean) => {
      sealTimers.splice(0).forEach(clearTimeout);
      const checks = qa("[data-seal-check]");
      const verdict = q("[data-seal-verdict]");
      const phase = q("[data-seal-phase]");

      checks.forEach((row) => {
        row.style.opacity = ".45";
        const dot = row.querySelector<HTMLElement>("[data-seal-dot]");
        const ok = row.querySelector<HTMLElement>("[data-seal-ok]");
        if (dot) dot.style.background = "var(--cr-border-strong)";
        if (ok) ok.style.opacity = "0";
      });
      if (verdict) {
        verdict.style.opacity = "0";
        verdict.style.transform = "translateY(8px)";
      }
      if (phase) {
        phase.textContent = "Sealed";
        phase.style.color = "var(--cr-fg-subtle)";
      }

      sealTimers.push(
        setTimeout(() => {
          if (phase) {
            phase.textContent = "Checking";
            phase.style.color = "var(--cr-fg-muted)";
          }
        }, 600),
      );

      checks.forEach((row, i) => {
        sealTimers.push(
          setTimeout(() => {
            row.style.opacity = "1";
            const dot = row.querySelector<HTMLElement>("[data-seal-dot]");
            const ok = row.querySelector<HTMLElement>("[data-seal-ok]");
            if (dot) dot.style.background = "var(--cr-mint)";
            if (ok) ok.style.opacity = "1";
          }, 900 + i * 520),
        );
      });

      const afterChecks = 900 + checks.length * 520;
      sealTimers.push(
        setTimeout(() => {
          if (verdict) {
            verdict.style.opacity = "1";
            verdict.style.transform = "translateY(0)";
          }
          if (phase) {
            phase.textContent = "Verified";
            phase.style.color = "var(--cr-mint)";
          }
        }, afterChecks + 350),
      );

      if (loop) sealTimers.push(setTimeout(() => runSeal(true), afterChecks + 5600));
    };

    const replayDemoBtn = q("[data-cr-replay-demo]");
    if (replayDemoBtn) {
      const h = () => runDemo(true);
      replayDemoBtn.addEventListener("click", h);
      cleanups.push(() => replayDemoBtn.removeEventListener("click", h));
    }
    const replayScanBtn = q("[data-cr-replay-scanner]");
    if (replayScanBtn) {
      const h = () => runScanner(true);
      replayScanBtn.addEventListener("click", h);
      cleanups.push(() => replayScanBtn.removeEventListener("click", h));
    }
    const replaySealBtn = q("[data-cr-replay-seal]");
    if (replaySealBtn) {
      const h = () => runSeal(true);
      replaySealBtn.addEventListener("click", h);
      cleanups.push(() => replaySealBtn.removeEventListener("click", h));
    }

    // ── sealed-record example modal ────────────────────────────────────────────
    // Opened from the "what you checked, when you checked it…" phrase in #proof.
    // The markup ships inside HOMEPAGE_HTML (see SEALED_RECORD_MODAL_HTML), so
    // it is wired here the same way as the demo replay buttons rather than as a
    // React portal — the page is one injected string and mixing the two would
    // put the dialog outside [data-cr-root]'s theme tokens.
    const sealDoc = q("[data-cr-sealdoc-overlay]");
    if (sealDoc) {
      const panel = sealDoc.querySelector<HTMLElement>("[data-cr-sealdoc-panel]");
      const openers = qa("[data-cr-sealdoc-open]");
      const closer = sealDoc.querySelector<HTMLElement>("[data-cr-sealdoc-close]");
      let lastFocused: HTMLElement | null = null;

      const openSealDoc = () => {
        lastFocused = document.activeElement as HTMLElement | null;
        sealDoc.style.display = "flex";
        // Let the browser paint display:flex before transitioning opacity in,
        // otherwise the fade is skipped entirely.
        requestAnimationFrame(() => {
          sealDoc.style.opacity = "1";
          if (panel) panel.style.transform = "translateY(0) scale(1)";
        });
        document.body.style.overflow = "hidden";
        closer?.focus();
      };

      const closeSealDoc = () => {
        sealDoc.style.opacity = "0";
        if (panel) panel.style.transform = "translateY(12px) scale(.985)";
        document.body.style.overflow = "";
        window.setTimeout(() => {
          sealDoc.style.display = "none";
        }, 220);
        lastFocused?.focus();
      };

      openers.forEach((btn) => {
        btn.addEventListener("click", openSealDoc);
        cleanups.push(() => btn.removeEventListener("click", openSealDoc));
      });
      if (closer) {
        closer.addEventListener("click", closeSealDoc);
        cleanups.push(() => closer.removeEventListener("click", closeSealDoc));
      }

      // Backdrop click, but not a click that started inside the panel (dragging
      // to select the hashes would otherwise close the dialog on mouse-up).
      const onBackdrop = (e: MouseEvent) => {
        if (e.target === sealDoc) closeSealDoc();
      };
      sealDoc.addEventListener("click", onBackdrop);
      cleanups.push(() => sealDoc.removeEventListener("click", onBackdrop));

      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape" && sealDoc.style.display === "flex") closeSealDoc();
      };
      document.addEventListener("keydown", onKey);
      cleanups.push(() => document.removeEventListener("keydown", onKey));

      // The overlay is fixed-position; if the component unmounts while it is
      // open, body scroll would stay locked for the rest of the session.
      cleanups.push(() => {
        document.body.style.overflow = "";
      });
    }

    runDemo(true);
    runScanner(true);
    runSeal(true);

    // ── wire placeholder links to real destinations ────────────────────────────
    qa("a").forEach((a) => {
      const text = (a.textContent || "").replace(/\s+/g, " ").trim();
      const mapped = LINK_MAP[text];
      if (mapped) a.setAttribute("href", mapped);
    });

    // Delegate internal (same-origin route) navigation through the SPA router so
    // clicks don't trigger a full page reload. Hash, mailto, and external links
    // fall through to default handling.
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
        return;
      }
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (href.startsWith("/")) {
        e.preventDefault();
        navigate(href);
      }
    };
    root.addEventListener("click", onClick);
    cleanups.push(() => root.removeEventListener("click", onClick));

    return () => {
      demoTimers.splice(0).forEach(clearTimeout);
      scanTimers.splice(0).forEach(clearTimeout);
      sealTimers.splice(0).forEach(clearTimeout);
      io?.disconnect();
      cleanups.forEach((fn) => fn());
    };
  }, [navigate]);

  return (
    <>
      <MarketingHead />
      <style dangerouslySetInnerHTML={{ __html: HOMEPAGE_STYLE }} />
      <LandingNav />
      <main id="main-content">
        <div ref={hostRef} dangerouslySetInnerHTML={{ __html: HOMEPAGE_HTML }} />
      </main>
      <LandingFooter />
    </>
  );
};

export default Index;
